import React, { useState } from 'react';
import { Lock, Save, CheckCircle2, AlertCircle, ShieldCheck } from 'lucide-react';
import { authApi } from '../../lib/api';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

export default function AdminSecurity() {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [error, setError] = useState('');
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setOk(false);
    if (form.new_password.length < 6) return setError('New password must be at least 6 characters.');
    if (form.new_password !== form.confirm) return setError('New passwords do not match.');
    setBusy(true);
    try {
      await authApi.changePassword({ current_password: form.current_password, new_password: form.new_password });
      setOk(true);
      setForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Could not change password');
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-xl font-bold text-[#0b1f3a]">Security</h2>
        <p className="text-slate-500 text-sm mt-1">Change your admin password. Choose something strong.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-5">
            <span className="icon-tile !w-9 !h-9"><Lock size={16} /></span>
            <h3 className="font-semibold text-[#0b1f3a]">Change password</h3>
          </div>

          {error && (
            <div className="mb-4 flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg px-3 py-2 text-sm">
              <AlertCircle size={16} className="mt-0.5" /> {error}
            </div>
          )}
          {ok && (
            <div className="mb-4 flex items-start gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg px-3 py-2 text-sm">
              <CheckCircle2 size={16} className="mt-0.5" /> Password updated successfully.
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label className="field-label">Current password</Label>
              <Input type="password" required value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} />
            </div>
            <div>
              <Label className="field-label">New password</Label>
              <Input type="password" required minLength={6} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} />
              <p className="text-xs text-slate-500 mt-1">Minimum 6 characters. Use a passphrase for extra security.</p>
            </div>
            <div>
              <Label className="field-label">Confirm new password</Label>
              <Input type="password" required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} />
            </div>
          </div>

          <button type="submit" disabled={busy} className="btn-primary mt-6 disabled:opacity-60">
            <Save size={16} /> {busy ? 'Saving...' : 'Update password'}
          </button>
        </form>

        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="icon-tile !w-9 !h-9"><ShieldCheck size={16} /></span>
            <h3 className="font-semibold text-[#0b1f3a]">Security tips</h3>
          </div>
          <ul className="space-y-3 text-sm text-slate-600">
            <li>Use a password of at least 12 characters, mixing letters, numbers and symbols.</li>
            <li>Never share your admin password with anyone — our team will never ask for it.</li>
            <li>Do not reuse a password from other websites.</li>
            <li>Consider a password manager (1Password, Bitwarden, iCloud Keychain).</li>
            <li>Log out from shared or public computers.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
